import { useCallback, useEffect, useRef, useState } from 'react';
import type { ListTableRow, ListTableSort } from 'framework/ui/ui-builder';
import { isAbortError } from 'framework/api';
import { appendGroupedPage, buildInitialPageState, CollectionPage, mergeCollectionPage } from './collectionPage';
import { PageLoadDirection } from './collectionPagerTypes';
import type { UseAnyCollectionPagerParams } from './collectionPagerTypes';

interface LoadPageOverrides<Filters> {
  filters?: Filters;
  searchValue?: string;
  sort?: ListTableSort | null;
}

interface FailedPageRequest<Filters> {
  pageNumber: number;
  direction: PageLoadDirection;
  overrides?: LoadPageOverrides<Filters>;
}

type UseCollectionPagesParams<Row extends ListTableRow, Filters> = Pick<
  UseAnyCollectionPagerParams<Row, Filters>,
  'fetchPage' | 'fetchInitialPage' | 'pageSize' | 'collectionKey'
> & {
  enabled: boolean;
  isGrouped: boolean;
  filters: Filters;
  sort: ListTableSort | null;
  searchValue: string;
  isSearchPending: boolean;
};

const useCollectionPages = <Row extends ListTableRow, Filters>({
  fetchPage,
  fetchInitialPage,
  pageSize,
  collectionKey,
  enabled,
  isGrouped,
  filters,
  sort,
  searchValue,
  isSearchPending,
}: UseCollectionPagesParams<Row, Filters>) => {
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const fetchInitialPageRef = useRef(fetchInitialPage);
  fetchInitialPageRef.current = fetchInitialPage;
  const [page, setPage] = useState<CollectionPage<Row>>(buildInitialPageState<Row>);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const failedPageRef = useRef<FailedPageRequest<Filters>>({ pageNumber: 0, direction: PageLoadDirection.REPLACE });
  const loadingRef = useRef(false);
  const groupedPageRef = useRef<CollectionPage<Row> | null>(null);

  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const invalidateRequests = useCallback(() => {
    ++requestIdRef.current;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const startRequest = () => {
    invalidateRequests();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return { requestId: requestIdRef.current, signal: controller.signal };
  };

  const loadGroupedPages = async (targetPageNumber: number, overrides: LoadPageOverrides<Filters>) => {
    const { requestId, signal } = startRequest();
    let pending = targetPageNumber === 0 ? null : groupedPageRef.current;
    groupedPageRef.current = pending;
    let nextPage = pending ? targetPageNumber : 0;
    const currentFilters = overrides.filters ?? filters;
    const currentSearch = overrides.searchValue ?? searchValue;
    const currentSort = overrides.sort !== undefined ? overrides.sort : sort;

    loadingRef.current = true;
    setIsLoading(true);
    setIsLoadingMore(false);
    setIsLoadingPrevious(false);
    setError(null);
    try {
      do {
        const fetcher = nextPage === 0 ? (fetchInitialPageRef.current ?? fetchPageRef.current) : fetchPageRef.current;
        const response = await fetcher({
          pageNumber: nextPage,
          pageSize,
          filters: currentFilters,
          searchValue: currentSearch,
          sort: currentSort ? { [currentSort.columnId]: currentSort.direction } : undefined,
          signal,
        });
        if (requestId !== requestIdRef.current || signal.aborted) return;

        pending = appendGroupedPage(pending ?? buildInitialPageState<Row>(), response, nextPage, currentSearch);
        groupedPageRef.current = pending;
        nextPage += 1;
      } while (nextPage < pending.totalPages);

      setPage(pending);
      groupedPageRef.current = null;
    } catch (cause) {
      if (requestId === requestIdRef.current && !signal.aborted && !isAbortError(cause)) {
        failedPageRef.current = { pageNumber: nextPage, direction: PageLoadDirection.REPLACE, overrides };
        setError(cause instanceof Error ? cause : new Error('Could not load records.'));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        abortControllerRef.current = null;
        setIsLoading(false);
      }
    }
  };

  const loadPage = async (
    targetPageNumber: number,
    direction: PageLoadDirection,
    overrides: LoadPageOverrides<Filters> = {},
  ): Promise<void> => {
    if (!enabled) return;
    if (isGrouped) return loadGroupedPages(targetPageNumber, overrides);
    const { requestId, signal } = startRequest();
    const isInitialPage = direction === PageLoadDirection.REPLACE;
    const currentFilters = overrides.filters ?? filters;
    const currentSearch = overrides.searchValue ?? searchValue;
    const currentSort = overrides.sort !== undefined ? overrides.sort : sort;

    setIsLoading(isInitialPage);
    setIsLoadingMore(direction === PageLoadDirection.NEXT);
    setIsLoadingPrevious(direction === PageLoadDirection.PREVIOUS);
    setError(null);
    loadingRef.current = true;

    try {
      const pageFetcher = isInitialPage ? (fetchInitialPageRef.current ?? fetchPageRef.current) : fetchPageRef.current;
      const response = await pageFetcher({
        pageNumber: targetPageNumber,
        pageSize,
        filters: currentFilters,
        searchValue: currentSearch,
        sort: currentSort ? { [currentSort.columnId]: currentSort.direction } : undefined,
        signal,
      });

      if (requestId !== requestIdRef.current || signal.aborted) return;

      setPage(previousPage => mergeCollectionPage(previousPage, response, direction, currentSearch));
    } catch (cause) {
      if (requestId === requestIdRef.current && !signal.aborted && !isAbortError(cause)) {
        failedPageRef.current = { pageNumber: targetPageNumber, direction, overrides };
        setError(cause instanceof Error ? cause : new Error('Could not load records.'));
      }
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        abortControllerRef.current = null;
        setIsLoading(false);
        setIsLoadingMore(false);
        setIsLoadingPrevious(false);
      }
    }
  };

  useEffect(() => {
    if (!enabled) {
      groupedPageRef.current = null;
      setPage(buildInitialPageState<Row>());
      setIsLoading(false);
      setIsLoadingMore(false);
      setIsLoadingPrevious(false);
      setError(null);
      loadingRef.current = false;
      return;
    }
    if (isSearchPending) {
      return invalidateRequests;
    }

    setPage(buildInitialPageState<Row>());
    loadPage(0, PageLoadDirection.REPLACE, { filters, searchValue, sort });
    return invalidateRequests;
    // Query changes always restart infinite loading from the first page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionKey, enabled, pageSize, searchValue, invalidateRequests, isSearchPending, filters, sort]);

  const hasPrevious = !page.hasReachedStart && page.firstPageNumber > 0;
  const hasMore = !page.hasReachedEnd && page.lastPageNumber + 1 < page.totalPages;

  const loadPrevious = () => {
    if (enabled && !loadingRef.current && hasPrevious && !isSearchPending) {
      return loadPage(page.firstPageNumber - 1, PageLoadDirection.PREVIOUS);
    }
    return undefined;
  };

  const loadMoreError = error && failedPageRef.current.direction === PageLoadDirection.NEXT ? error.message : undefined;

  const loadMore = () => {
    if (enabled && !loadingRef.current && hasMore && !isSearchPending && !loadMoreError) {
      return loadPage(page.lastPageNumber + 1, PageLoadDirection.NEXT);
    }
    return undefined;
  };

  return {
    page,
    isLoading,
    isLoadingMore,
    isLoadingPrevious,
    hasPrevious,
    hasMore,
    error,
    loadMoreError,
    retry: () =>
      isSearchPending || loadingRef.current
        ? undefined
        : loadPage(failedPageRef.current.pageNumber, failedPageRef.current.direction, failedPageRef.current.overrides),
    loadPrevious,
    loadMore,
    reload: () => (isSearchPending ? undefined : loadPage(0, PageLoadDirection.REPLACE)),
  };
};

export default useCollectionPages;
